import { Container, Title, Text, Stack, Button, Group, Accordion } from '@mantine/core'
import { IconArrowLeft } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'

export function GettingStartedPage() {
    const navigate = useNavigate()

    return (
        <Container size="md" py="xl">
            <Stack gap="xl">
                <Group gap="md">
                    <Button
                        variant="subtle"
                        leftSection={<IconArrowLeft size={16} />}
                        onClick={() => navigate('/help')}
                    >
                        Back to Help
                    </Button>
                </Group>

                <div>
                    <Title order={1} size="h1" mb="md">Getting Started with Spotter</Title>
                    <Text size="lg" c="dimmed">
                        Learn how to use Spotter to discover and share interesting things around you.
                    </Text>
                </div>

                <Accordion variant="separated" radius="md">
                    <Accordion.Item value="what-is-spotter">
                        <Accordion.Control>
                            <Title order={3} size="h4">What is Spotter?</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Text>
                                Spotter is a location-based app that helps you discover and share interesting things around you.
                                Whether it's a rare car, a celebrity sighting, a great food truck, or anything else worth noting,
                                Spotter keeps you connected to what's happening nearby.
                            </Text>
                        </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="browsing-spottings">
                        <Accordion.Control>
                            <Title order={3} size="h4">Browsing Spottings</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="sm">
                                <Text>
                                    <strong>View the Map:</strong> The main screen shows a map with all nearby spottings.
                                    Each spotting appears as a pin on the map.
                                </Text>
                                <Text>
                                    <strong>Browse the List:</strong> On mobile, swipe up from the bottom to see a list of all spottings.
                                    On desktop, the list appears in the sidebar.
                                </Text>
                                <Text>
                                    <strong>Filter by Category:</strong> Use the category filter to show only specific types of spottings
                                    (like Food, Vehicles, People, etc.).
                                </Text>
                                <Text>
                                    <strong>View Details:</strong> Click on any spotting pin or list item to see more details,
                                    including photos and descriptions.
                                </Text>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="creating-spottings">
                        <Accordion.Control>
                            <Title order={3} size="h4">Creating Spottings</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="sm">
                                <Text>
                                    <strong>Sign In First:</strong> You need to sign in to create spottings.
                                    You can browse without an account, but contributing requires authentication.
                                </Text>
                                <Text>
                                    <strong>Two Ways to Create:</strong>
                                </Text>
                                <Text pl="md">
                                    • Click the "Use My Location" button to create a spotting at your current location
                                </Text>
                                <Text pl="md">
                                    • Click anywhere on the map to create a spotting at that specific location
                                </Text>
                                <Text>
                                    <strong>Add Details:</strong> Fill in the category, description, and optionally add a photo
                                    to make your spotting more interesting and useful.
                                </Text>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="location-services">
                        <Accordion.Control>
                            <Title order={3} size="h4">Location Services</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="sm">
                                <Text>
                                    <strong>Allow Location Access:</strong> For the best experience, allow Spotter to access your location.
                                    This helps you see nearby spottings and create spottings at your current location.
                                </Text>
                                <Text>
                                    <strong>iOS Safari Users:</strong> If location isn't working, try tapping the "Use My Location" button
                                    to trigger the permission prompt.
                                </Text>
                                <Text>
                                    <strong>Privacy:</strong> Your location is only used to show you relevant spottings and to set the
                                    location of spottings you create. We don't track or store your location history.
                                </Text>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="categories">
                        <Accordion.Control>
                            <Title order={3} size="h4">Spotting Categories</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="sm">
                                <Text>
                                    <strong>Food & Drink:</strong> Food trucks, pop-up restaurants, great deals, or interesting culinary finds
                                </Text>
                                <Text>
                                    <strong>Vehicles:</strong> Rare cars, interesting motorcycles, unique vehicles, or transportation oddities
                                </Text>
                                <Text>
                                    <strong>People:</strong> Celebrity sightings, street performers, or notable public figures
                                </Text>
                                <Text>
                                    <strong>Events:</strong> Spontaneous gatherings, street performances, or temporary happenings
                                </Text>
                                <Text>
                                    <strong>Objects:</strong> Interesting street art, unusual items, or things worth noting
                                </Text>
                                <Text>
                                    <strong>Other:</strong> Anything else that doesn't fit the other categories but is worth sharing
                                </Text>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="tips">
                        <Accordion.Control>
                            <Title order={3} size="h4">Tips for Great Spottings</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="sm">
                                <Text>
                                    <strong>Be Descriptive:</strong> Add details that help others understand what makes your spotting interesting.
                                </Text>
                                <Text>
                                    <strong>Add Photos:</strong> A picture is worth a thousand words. Photos make spottings much more engaging.
                                </Text>
                                <Text>
                                    <strong>Choose the Right Category:</strong> Proper categorization helps others find relevant spottings.
                                </Text>
                                <Text>
                                    <strong>Be Respectful:</strong> Don't photograph people without permission, and respect private property.
                                </Text>
                                <Text>
                                    <strong>Stay Safe:</strong> Don't create spottings while driving, and be aware of your surroundings.
                                </Text>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            </Stack>
        </Container>
    )
} 